"use client"

import Image from "next/image"
import Link from "next/link"
import { createContext, useContext, useEffect, useRef, useState } from "react"
import { ChevronDown, ArrowRight } from "lucide-react"
import type { BusinessInfo, LandingPageProps } from "../_lib/landing-page.types"
import "./styles.css"

const ContactContext = createContext<BusinessInfo["contact"] | undefined>(undefined)

function useContact() {
  return useContext(ContactContext)
}

// Strip everything except digits — used for tel: and wa.me links.
function digitsOnly(value: string) {
  return value.replace(/[^\d]/g, "")
}

// Display phone with a leading "+" if it looks like an E.164 number.
function formatPhone(value: string) {
  const trimmed = value.trim()
  if (trimmed.startsWith("+")) return trimmed
  const digits = digitsOnly(trimmed)
  return digits ? `+${digits}` : trimmed
}

// Dune Pattern SVG Component
function DunePattern({ className = "", opacity = 0.3 }: { className?: string; opacity?: number }) {
  return (
    <svg
      className={className}
      viewBox="0 0 800 400"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity }}
      preserveAspectRatio="none"
    >
      <path d="M0 200 Q200 100 400 200 T800 200" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M0 220 Q200 120 400 220 T800 220" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M0 240 Q200 140 400 240 T800 240" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M0 260 Q200 160 400 260 T800 260" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M0 280 Q200 180 400 280 T800 280" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M0 300 Q200 200 400 300 T800 300" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  )
}

// Hero Section - Video already contains text overlay
function HeroSection() {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }, [])

  return (
    <section className="relative h-screen w-full overflow-hidden bg-[#1B1B1B]">
      {/* Video Background - Contains text overlay */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        poster="/landing-pages/drc/images/DR-landing-page-poster.webp"
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/landing-pages/drc/images/DRC_Landing_page_compressed.mp4" type="video/mp4" />
      </video>

      {/* H1 + CTAs - Bottom positioned */}
      <div className="absolute bottom-24 lg:bottom-32 left-0 right-0">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-16">
          <h1 className="sr-only">Dunes Racquet Club — Premium Tennis &amp; Padel in Jeddah</h1>
          <div
            className="flex flex-col sm:flex-row gap-4 animate-fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            <Link
              href="/book"
              className="inline-flex items-center justify-center bg-[#FF6B01] text-black font-kinetica text-base h-14 px-8 rounded-full hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
            >
              Book Now
            </Link>
            <Link
              href="#facilities"
              className="inline-flex items-center justify-center border-2 border-white text-white font-kinetica text-base h-14 px-8 rounded-full hover:bg-white/10 transition-colors duration-200"
            >
              Explore Facilities
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-white/70" />
      </div>
    </section>
  )
}

// Why Dunes Section
function WhyDunesSection() {
  return (
    <section id="about" className="relative bg-[#F5F6F6] py-12 lg:py-16 overflow-hidden">
      {/* Decorative Dune Pattern */}
      <DunePattern
        className="absolute bottom-0 right-0 w-[600px] h-[300px] text-[#E0E1E1]"
        opacity={0.3}
      />

      <div className="max-w-[1200px] mx-auto px-8 text-center relative z-10">
        {/* Section Label */}
        <span className="font-aeonik font-bold text-[#ABABAB] text-xs tracking-[3px] uppercase mb-4 block">
          WHY DUNES?
        </span>

        {/* Headline */}
        <h2 className="font-kinetica text-[32px] sm:text-[40px] lg:text-[48px] font-bold text-[#1B1B1B] leading-[1.1] max-w-[900px] mx-auto mb-8">
          A racquet oasis for people who move, compete, and connect.
        </h2>

        {/* Accent Line */}
        <div className="w-20 h-1 bg-[#FF6B01] mx-auto mb-8"></div>

        {/* Tagline */}
        <h3 className="font-kinetica text-[20px] sm:text-[24px] lg:text-[28px] font-bold text-[#FF6B01]">
          Courts. Community. Culture.
        </h3>
      </div>
    </section>
  )
}

// Facilities Section
function FacilitiesSection() {
  return (
    <section id="facilities" className="bg-[#1B1B1B] py-12 lg:py-16">
      <div className="max-w-[1200px] mx-auto px-8">
        {/* Header */}
        <div className="mb-12">
          <span className="font-aeonik font-bold text-[#ABABAB] text-xs tracking-[3px] uppercase block mb-4">
            OUR FACILITIES
          </span>
          <h2 className="font-kinetica text-[32px] sm:text-[40px] lg:text-[48px] font-bold text-white leading-none">
            EVERYTHING YOU NEED
          </h2>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Large Left Image - Full height */}
          <div className="relative group overflow-hidden rounded-lg aspect-[3/4] lg:aspect-[3/4]">
            <Image
              src="/landing-pages/drc/images/tennis-drc.webp"
              alt="Tennis courts at Dunes Racquet Club in Jeddah"
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#FF6B01]/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-6 left-6">
              <span className="font-kinetica text-[24px] lg:text-[28px] font-bold text-white">
                TENNIS COURT
              </span>
            </div>
          </div>

          {/* Right Column - Two stacked images */}
          <div className="flex flex-col gap-6">
            {/* Top Right Image */}
            <div className="relative group overflow-hidden rounded-lg aspect-video flex-1">
              <Image
                src="/landing-pages/drc/images/DR-landing-page-04-c.webp"
                alt="Padel courts at Dunes Racquet Club in Jeddah"
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <span className="font-kinetica text-[20px] lg:text-[24px] font-bold text-white">
                  PADEL COURTS
                </span>
              </div>
            </div>

            {/* Bottom Right Image */}
            <div className="relative group overflow-hidden rounded-lg aspect-video flex-1">
              <Image
                src="/landing-pages/drc/images/DR-landing-page-02-c.webp"
                alt="Conditioning gym at Dunes Racquet Club"
                fill
                className="object-cover object-bottom transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          </div>
        </div>

        {/* Facilities List */}
        <div className="text-center mb-8">
          <p className="font-aeonik text-white text-lg">
            <span className="text-[#ABABAB]">Padel</span>
            <span className="text-[#ABABAB] mx-3">•</span>
            <span className="text-[#ABABAB]">Tennis</span>
            <span className="text-[#ABABAB] mx-3">•</span>
            <span className="text-[#ABABAB]">Conditioning Gym</span>
            <span className="text-[#ABABAB] mx-3">•</span>
            <span className="text-[#ABABAB]">Dunes Café</span>
            <span className="text-[#ABABAB] mx-3">•</span>
            <span className="text-[#ABABAB]">Pro Shop</span>
          </p>
        </div>

      </div>
    </section>
  )
}

// Community Section
function CommunitySection() {
  return (
    <section className="relative bg-[#FF6B01] py-12 lg:py-16 overflow-hidden">
      {/* Decorative Dune Pattern */}
      <DunePattern
        className="absolute top-0 right-0 w-full h-full text-[#F7934D]"
        opacity={0.4}
      />

      <div className="max-w-[1200px] mx-auto px-8 text-center relative z-10">
        {/* Section Label */}
        <span className="font-aeonik font-bold text-[#1B1B1B]/50 text-xs tracking-[3px] uppercase block mb-4">
          COMMUNITY
        </span>

        {/* Headlines */}
        <h2 className="font-kinetica text-[32px] sm:text-[40px] lg:text-[48px] font-bold leading-[0.95] mb-8">
          <span className="text-black block mb-2">BUILT FOR PLAYERS.</span>
          <span className="text-white block">POWERED BY COMMUNITY.</span>
        </h2>

        {/* Description */}
        <p className="font-aeonik text-[#1B1B1B] text-lg lg:text-xl max-w-[600px] mx-auto">
          Leagues, events, youth programs, running club, and more.
        </p>
      </div>
    </section>
  )
}

// Café Section
function CafeSection() {
  return (
    <section id="cafe" className="bg-[#F5F6F6]">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left Image */}
        <div className="relative aspect-square lg:aspect-auto min-h-[400px] lg:min-h-[600px]">
          <Image
            src="/landing-pages/drc/images/shop-drc.webp"
            alt="Pro shop at Dunes Racquet Club in Jeddah"
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>
        </div>

        {/* Right Content */}
        <div className="flex items-center px-8 lg:px-20 py-16 lg:py-0">
          <div>
            {/* Section Label */}
            <span className="font-aeonik font-bold text-[#ABABAB] text-xs tracking-[3px] uppercase block mb-4">
              DUNES CAFÉ
            </span>

            {/* Headline */}
            <h2 className="font-kinetica text-[32px] sm:text-[40px] lg:text-[48px] font-bold text-[#1B1B1B] leading-[1.05] mb-6">
              FUEL THE MOVEMENT.
            </h2>

            {/* Description */}
            <p className="font-aeonik text-[#1B1B1B]/70 text-lg lg:text-xl mb-6">
              Fresh smoothies, protein shakes and coffee.
            </p>

            {/* Menu Items }
            <p className="font-aeonik text-[#1B1B1B] text-base mb-8">
              Smoothies • Coffee • Bowls • Sandwiches • Protein Shakes
            </p> */}

            {/* CTA */}
            <Link
              href="#"
              className="inline-flex items-center gap-2 font-kinetica text-[#FF6B01] text-base group"
            >
              View Menu
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

// Lazy Map Component - loads iframe only when in viewport
function LazyMap() {
  const [isLoaded, setIsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (mapRef.current) {
      observer.observe(mapRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mapRef}
      className="rounded-2xl overflow-hidden shadow-sm h-[400px] lg:h-auto min-h-[400px] bg-[#E0E1E1]"
    >
      {isLoaded ? (
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4148.519273622191!2d39.12596921119594!3d21.62348096700339!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3db1d74a7e629%3A0xb6f213ddb2b0170b!2zRHVuZXMgUmFjcXVldCBDbHViIHwg2K_ZitmI2YbYsiDYsdin2YPZitiqINmD2YTZiNio!5e0!3m2!1sen!2ssa!4v1766485432295!5m2!1sen!2ssa"
          width="100%"
          height="100%"
          style={{ border: 0, minHeight: '400px' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Dunes Racquet Club Location"
        />
      ) : (
        <div className="w-full h-full min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <svg className="w-12 h-12 text-[#ABABAB] mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-[#ABABAB] font-aeonik text-sm">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Contact block — pulls phone + whatsapp from business_settings via context.
// Renders both rows when the numbers differ, or a single Contact row otherwise.
// Falls back to the historic Dunes number if no business_info is configured.
const FALLBACK_PHONE = "+966504525907"

function ContactBlock() {
  const contact = useContact()

  const rawPhone = contact?.phone?.trim() || ""
  const rawWhatsapp = contact?.whatsapp?.trim() || ""

  const phone = rawPhone || (rawWhatsapp ? "" : FALLBACK_PHONE)
  const whatsapp = rawWhatsapp

  const phoneDigits = phone ? digitsOnly(phone) : ""
  const whatsappDigits = whatsapp ? digitsOnly(whatsapp) : ""
  const showSeparate = Boolean(phone && whatsapp && phoneDigits !== whatsappDigits)

  return (
    <div className="mb-8 space-y-4">
      {phone ? (
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#FF6B01]/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-[#FF6B01]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div>
            <p className="font-aeonik font-bold text-[#1B1B1B] text-lg mb-1">
              {showSeparate ? "Phone" : "Contact"}
            </p>
            <a
              href={`tel:${phoneDigits ? `+${phoneDigits}` : phone}`}
              className="font-aeonik text-[#FF6B01] hover:underline"
            >
              {formatPhone(phone)}
            </a>
          </div>
        </div>
      ) : null}

      {whatsapp && (showSeparate || !phone) ? (
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.81 11.81 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
            </svg>
          </div>
          <div>
            <p className="font-aeonik font-bold text-[#1B1B1B] text-lg mb-1">WhatsApp</p>
            <a
              href={`https://wa.me/${whatsappDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-aeonik text-[#25D366] hover:underline"
            >
              {formatPhone(whatsapp)}
            </a>
          </div>
        </div>
      ) : null}
    </div>
  )
}

// Location Section
function LocationSection() {
  return (
    <section id="location-section" className="bg-[#F5F6F6] py-12 lg:py-16">
      <div className="max-w-[1200px] mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-aeonik font-bold text-[#ABABAB] text-xs tracking-[3px] uppercase block mb-4">
            FIND US
          </span>
          <h2 className="font-kinetica text-[32px] sm:text-[40px] lg:text-[48px] font-bold text-[#1B1B1B] leading-none">
            OUR LOCATION
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Location Details */}
          <address className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm not-italic">
            {/* Club Name */}
            <h3 className="font-kinetica text-[24px] lg:text-[28px] font-bold text-[#1B1B1B] mb-6">
              Dunes Racquet Club
            </h3>

            {/* Address */}
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#FF6B01]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-[#FF6B01]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="font-aeonik font-bold text-[#1B1B1B] text-lg mb-1">Address</p>
                <p className="font-aeonik text-[#1B1B1B]/70">
                  6925 Al Safaa, 4823<br />
                  An Nahdah District<br />
                  Jeddah 23615, Saudi Arabia
                </p>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#FF6B01]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-[#FF6B01]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-aeonik font-bold text-[#1B1B1B] text-lg mb-1">Every Day</p>
                <p className="font-aeonik text-[#1B1B1B]/70">
                  10:00 AM - 12:00 AM
                </p>
              </div>
            </div>

            <ContactBlock />


            {/* Get Directions Button */}
            <a
              href="https://maps.app.goo.gl/Bwb7sMf2jfBwpdsAA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-[#FF6B01] text-black font-kinetica text-base h-14 px-8 rounded-full hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 w-full sm:w-auto"
            >
              Get Directions
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </address>

          {/* Right: Map */}
          <LazyMap />
        </div>
      </div>
    </section>
  )
}

// CTA Section
function CTASection() {
  return (
    <section className="relative bg-black py-12 lg:py-16 overflow-hidden">
      {/* Decorative Dune Pattern */}
      <DunePattern
        className="absolute bottom-0 left-0 w-full h-[200px] text-[#1B1B1B]"
        opacity={0.3}
      />

      <div className="max-w-[1200px] mx-auto px-8 text-center relative z-10">
        {/* Headline */}
        <h2 className="font-kinetica text-[32px] sm:text-[40px] lg:text-[48px] font-bold text-white leading-none mb-6">
          READY TO PLAY?
        </h2>

        {/* Subtext */}
        <p className="font-aeonik text-[#ABABAB] text-lg lg:text-xl mb-10 max-w-[600px] mx-auto">
          Simple bookings for padel, tennis, coaching, and classes.
        </p>

        {/* CTA */}
        <Link
          href="/auth/sign-in"
          className="inline-flex items-center justify-center bg-[#FF6B01] text-black font-kinetica text-base h-14 px-8 rounded-full hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
        >
          Book Now
        </Link>
      </div>
    </section>
  )
}

export default function DrcLandingPage({ businessInfo }: LandingPageProps = { tenant: null, config: {} }) {
  const contact = (businessInfo as BusinessInfo | undefined)?.contact

  return (
    <ContactContext.Provider value={contact}>
    <div className="min-h-screen bg-[#1B1B1B] overflow-x-hidden">
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }

        html, body {
          overscroll-behavior-y: none;
        }

        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-up {
          animation: fade-up 0.8s ease-out forwards;
          opacity: 0;
        }

        /* Scroll reveal animations */
        @keyframes reveal {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        section {
          animation: reveal 0.6s ease-out forwards;
        }
      `}</style>

      <HeroSection />
      <WhyDunesSection />
      <FacilitiesSection />
      <CommunitySection />
      <CafeSection />
      <LocationSection />
      <CTASection />
    </div>
    </ContactContext.Provider>
  )
}